<template>
    <div ref="stageArea">
        <Modal ref="modal" v-model="startModalOpen">
            <div class="info-box-modal-content checklist disable-info-icon">
                <h4 class="title">
                    Anleitung
                </h4>
                <p>
                    Klicke dich durch die {{ questionCount }} Fragen und ordne sie durch
                    Ziehen dem passenden Feld zu.
                </p>
                <p class="image-wrapper">
                    <img class="full-width block" :src="manualImage">
                </p>
                <p class="text-center">
                    <ControlButton active data-close>
                        Checkliste erstellen
                    </ControlButton>
                </p>
            </div>
        </Modal>
        <div class="side p-x p-y checklist-header">
            <h2>
                {{ config.title }}
            </h2>
        </div>
        <transition-group
            appear
            :css="false"
            name="list"
            tag="ul"
            class="styleless checklist-wrapper"
        >
            <li
                v-for="(category, categoryIndex) in config.categories" :key="category.name"
                ref="checklist"
                class="checklist"
            >
                <div class="image-wrapper">
                    <div class="title">
                        {{ category.name }}
                    </div>
                    <img class="block full-width" :src="category.image">

                    <transition-group
                        name="fade"
                        appear
                        tag="ul"
                        class="styleless category-item-list-wrapper"
                    >
                        <li
                            v-for="listItem in correctCards[categoryIndex]" :key="listItem"
                            class="list-item"
                        >
                            {{ listItem }}
                        </li>
                    </transition-group>
                </div>
            </li>
        </transition-group>
        <div
            v-if="dragItems.length > 0"
            class="drag-item-wrapper"
        >
            <div class="arrow prev" @click="slider.prev()" />
            <div ref="dragItem" class="drag-item">
                {{ dragItem.text }}
            </div>
            <div class="arrow next" @click="slider.next()" />
        </div>
    </div>
</template>

<script>
import gsap from 'gsap';
import Draggable from 'gsap/Draggable';
import Slider from '@kids/lib/class/Slider';
import {shuffle} from '@kids/lib/transform/array';
import SlideState from '@/mixins/SlideState';
import manualImage from '@/assets/modules/2.3/dd_anleitung.png';

gsap.registerPlugin(Draggable);

export default {
    name: 'FlashCards',
    mixins: [SlideState],
    props: {
        config: {
            type: Object,
            required: true
        }
    },
    data: () => ({
        startModalOpen: false,
        manualImage,
        dragItemIndex: 0,
        dragItems: [],
        slider: null,
        correctCards: []
    }),
    computed: {
        questionCount()
        {
            return this.dragItems.length;
        },
        dragItem()
        {
            return this.dragItems[this.dragItemIndex];
        }
    },
    created()
    {
        this.config.categories.map((element, categoryIndex) =>
            element.list.forEach((text) =>
            {
                this.dragItems.push(
                    {
                        text,
                        categoryIndex
                    }
                );
            }));

        this.dragItems = shuffle(this.dragItems);
    },
    mounted()
    {
        const state = this.getState();

        if (state)
        {
            this.correctCards = state.correctCards;
            this.dragItems = state.dragItems;
        }

        if (this.dragItems.length === 0)
        {
            this.$emit('finished');
            return;
        }

        this.createSlider();

        this.startModalOpen = true;
        this.createDraggable(this.$refs.dragItem);
    },
    methods: {
        createSlider()
        {
            this.slider = new Slider(
                this.dragItems.length,
                true
            );

            this.slider.on('change', (event) =>
            {
                this.dragItemIndex = event.index;
            });
        },
        createDraggable(dragElement)
        {
            [this._draggable] = Draggable.create([dragElement], {
                bounds: this.$parent.$el,
                onRelease: this.release
            });
        },
        release()
        {
            let targetHitted = false;

            this.$refs.checklist.forEach((item, index) =>
            {
                const hitDetected = this._draggable.hitTest(item, '75%');

                if (hitDetected)
                {
                    targetHitted = index;
                }
            });

            if (this.dragItem.categoryIndex === targetHitted)
            {
                this.$audioController.play('positive', {volume: 0.5});
                const {text, categoryIndex} = this.dragItem;
                const timeline = gsap.timeline();

                if (!this.correctCards[categoryIndex])
                {
                    this.$set(this.correctCards, categoryIndex, []);
                }
                this.correctCards[categoryIndex].push(text);

                timeline
                    .to(
                        this._draggable.target, {
                            opacity: 0,
                            duration: 0.5
                        },
                        0
                    )
                    .set(this._draggable.target, {x: 0, y: 0})
                    .to(
                        this._draggable.target, {
                            opacity: 1,
                            duration: 0.3,
                            onStart: () =>
                            {
                                this.dragItems.splice(this.dragItemIndex, 1);
                                this.saveState(this.createState());

                                if (this.dragItems.length <= 0)
                                {
                                    this.$emit('finished');
                                    return;
                                }

                                this.dragItemIndex = 0;
                                this.createSlider();
                            }
                        }
                    );
            }
            else
            {
                this.$audioController.play('negative', {volume: 0.5});
                gsap.to(
                    this._draggable.target, {x: 0, y: 0, duration: 0.2}
                );
            }
        },
        createState()
        {
            const {correctCards, dragItems} = this;

            return {
                correctCards,
                dragItems
            };
        }
    }
};
</script>

<style lang="scss" src="./CheckList.scss" />

